import React, { useState, useEffect } from "react";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { Modal, Button } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import config from '../../config.js';
import './Likes.css';
import UsernameModal from "../../layouts/UsernameModal.js";

const Likes = ({ quillId }) => {
  const [likes, setLikes] = useState([]);
  const [liked, setLiked] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated, loginWithRedirect } = useAuth0();

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const claims = await getIdTokenClaims();
        setUserId(claims?.username);
      } catch (error) {
        console.error("Error fetching user ID:", error);
      }
    };
    if (isAuthenticated) fetchUserId();
  }, [isAuthenticated, getIdTokenClaims]);

  useEffect(() => {
    if (quillId) fetchLikes();
  }, [quillId]);

  const fetchLikes = async () => {
    setLoading(true);
    try {
      if (!quillId) return;

      let token = null;
      if (isAuthenticated) {
        try {
          token = await getAccessTokenSilently();
        } catch {
          // ignore token error, fetch likes without auth
        }
      }

      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(`${config.apiUrl}/${config.apiStage}/likes?quill_id=${quillId}`, { headers });
      const data = await response.json();
      setLikes(Array.isArray(data) ? data : []);

      if (isAuthenticated && userId) {
        setLiked(data?.some(username => username.toLowerCase() === userId.toLowerCase()));
      }
    } catch (error) {
      console.error("Error fetching likes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (isAuthenticated && !userId) {
      const confirmSetUsername = window.confirm("You must set a username to react! Would you like to set one now?");
      if (confirmSetUsername) setShowUsernameModal(true);
      return;
    }

    let token;
    try {
      token = await getAccessTokenSilently();
    } catch {
      const currentPath = window.location.pathname;
      return loginWithRedirect({
        redirectUri: `${window.location.origin}/callback?redirectTo=${encodeURIComponent(currentPath)}`,
      });
    }

    try {
      if (!quillId) return;
      const method = liked ? "DELETE" : "POST";
      await fetch(`${config.apiUrl}/${config.apiStage}/like`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quill_id: quillId }),
      });
      setLiked(!liked);
      fetchLikes();
    } catch (error) {
      console.error("Error liking/unliking the post:", error);
    }
  };

  const handleUsernameUpdate = async () => {
    const currentPath = window.location.pathname;
    await loginWithRedirect({
      redirectUri: `${window.location.origin}/callback?redirectTo=${encodeURIComponent(currentPath)}`,
    });
  };

  const toggleLikesModal = () => setShowLikesModal(!showLikesModal);

  const handleLogin = async () => {
    setShowLoginModal(false);
    const currentPath = window.location.pathname;
    loginWithRedirect({
      redirectUri: `${window.location.origin}/callback?redirectTo=${encodeURIComponent(currentPath)}`,
    });
  };

  const firstThreeLikes = Array.isArray(likes) ? likes.slice(0, 2) : [];
  const hasMoreLikes = likes.length > 2;

  return (
    <>
      <div className="flex flex-col items-center">
        <button onClick={handleLike} className="flex flex-col items-center" style={{ width: '70px' }}>
          {liked ? <FaHeart size={24} color="red" /> : <FaRegHeart size={24} color="gray" />}
          <span className="text-black">{likes.length}</span>
        </button>

        <div className="likes-list text-black">
          {firstThreeLikes.map((username, index) => (
            <span key={index} className="username text-black" onClick={toggleLikesModal}>
              {username}{index < firstThreeLikes.length - 1 ? ", " : ""}
            </span>
          ))}
          {hasMoreLikes && <span className="username text-black" onClick={toggleLikesModal}>...</span>}
        </div>
      </div>

      {/* Login Modal */}
      <Modal isOpen={showLoginModal} toggle={() => setShowLoginModal(false)} className="modal-bottom text-black">
        <div className="modal-header text-black">
          <h5 className="modal-title text-black">Login to react!</h5>
          <button type="button" className="close" onClick={() => setShowLoginModal(false)}>
            <span>&times;</span>
          </button>
        </div>
        <div className="modal-body text-black">
          <p className="text-black">You need to log in to react to this post. Do you want to log in?</p>
        </div>
        <div className="modal-footer text-black">
          <Button color="primary" onClick={handleLogin}>Login</Button>
          <Button color="secondary" onClick={() => setShowLoginModal(false)}>Cancel</Button>
        </div>
      </Modal>

      {showUsernameModal && (
        <UsernameModal
          isOpen={showUsernameModal}
          onClose={() => setShowUsernameModal(false)}
          onSuccess={handleUsernameUpdate}
          onUsernameSet={(newUsername) => {
            setUserId(newUsername);
            setShowUsernameModal(false);
            handleLike();
          }}
        />
      )}

      {/* Likes Modal */}
      <Modal isOpen={showLikesModal} toggle={toggleLikesModal} className="modal-bottom text-black">
        <div className="modal-header text-black">
          <h5 className="modal-title text-black">Likes</h5>
          <button type="button" className="close" onClick={toggleLikesModal}>
            <span>&times;</span>
          </button>
        </div>
        <div className="modal-body text-black">
          {likes.length > 0 ? (
            likes.map((username, index) => (
              <div key={index} className="username text-black mb-2">{username}</div>
            ))
          ) : (
            <div className="text-black">No likes yet.</div>
          )}
        </div>
        <div className="modal-footer text-black">
          <Button color="secondary" onClick={toggleLikesModal}>Close</Button>
        </div>
      </Modal>
    </>
  );
};

export default Likes;
